import { useTheme } from "@mui/material";
import { createSxStyles } from "@common/createSxStyles";

export const usePaneLayoutStyles = () => {
    const theme = useTheme();

    return createSxStyles({
        header: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.5),
            padding: theme.spacing(1, 1.5),
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
            minHeight: 44,
        },
        // What the pane puts beside its controls and the controls themselves, as one run of buttons.
        headerActions: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.5),
            // The theme fills every SvgIcon with primary.main, and an explicit fill beats the colour an icon
            // would otherwise inherit from its button, so naming the colour alone leaves them as they were.
            // The icons that ship as their own svg draw themselves in primary.main instead of taking a
            // colour, so their strokes are named too. Kept to the pane's own buttons: what a pane puts in
            // its title, such as a task's type icon, keeps the colour it was drawn with.
            "& .MuiSvgIcon-root": {
                fill: theme.palette.primary.dark,
            },
            "& svg path[stroke]": {
                stroke: theme.palette.primary.dark,
            },
            // A pane's buttons get the same zero padding as the controls beside them. A padded button is taller
            // than a one-line title and stretches the header, so two panes side by side had their dividers at
            // different heights.
            "& .MuiIconButton-root": {
                padding: 0,
            },
        },
        headerTitle: {
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },
        paneTitle: {
            "& .MuiTypography-root": {
                fontSize: theme.typography.pxToRem(14),
                textTransform: "uppercase",
            },
        },
        controls: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.5),
        },
        headerIconButton: {
            padding: 0,
            marginRight: theme.spacing(1),
            color: theme.palette.primary.dark,
            // The theme fills every SvgIcon with primary.main, and an explicit fill beats the colour an icon
            // would otherwise inherit from its button, so naming the colour alone leaves them as they were.
            // The icons that ship as their own svg draw themselves in primary.main instead of taking a
            // colour, so their strokes are named too. Kept to the pane's own buttons: what a pane puts in
            // its title, such as a task's type icon, keeps the colour it was drawn with.
            "& .MuiSvgIcon-root": {
                fill: theme.palette.primary.dark,
            },
            "& svg path[stroke]": {
                stroke: theme.palette.primary.main,
            },

            "&:hover": {
                backgroundColor: theme.palette.grey[100],
            },
        },
        /**
         * Above the scrolling region and never part of it. It reserves the same scrollbar gutter as the content
         * beneath it, on a box that cannot overflow, which is what keeps columns in this slot lined up with the
         * columns scrolling underneath. The content's own top padding supplies the gap below.
         */
        fixedContent: {
            flexShrink: 0,
            overflowY: "hidden",
            scrollbarGutter: "stable",
            padding: theme.spacing(2, 2, 0, 2),
        },
        // Only beside fixed content: without it the gutter would appear the moment the content overflows and
        // shift the scrolling columns away from the fixed ones above them.
        contentBesideFixedContent: {
            scrollbarGutter: "stable",
        },
        // The pane's scrolling region, so a pane can be as long as it likes without its header or footer
        // moving. A pane that manages its own scrolling still can: its scroller takes the height and this
        // one never overflows.
        content: {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
        },
        // The pane's own inset, so nothing inside it starts with padding of its own. A viewer that has to
        // reach the pane's edges asks for noContentPadding instead of fighting this.
        contentPadded: {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            padding: theme.spacing(2),
        },
        // The room a field tooltip takes to the right of its input. It has to be a block box, not padding on the
        // flex content region: the form fields carry their own vertical margins, and those only collapse into
        // one gap between fields in block layout. As flex items they add up and double the spacing.
        fieldTooltipSpace: {
            paddingRight: theme.spacing(4),
        },
        // Full bleed rather than inside the content inset, so the rule above it spans the whole pane.
        footer: {
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(1),
            padding: theme.spacing(2),
            borderTop: `1px solid ${theme.palette.grey[100]}`,
        },
        footerButtons: {
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: theme.spacing(1),
        },
        footerStart: {
            marginRight: "auto",
        },
        // A line of its own under the buttons: beside them it took their width and wrapped their labels.
        footerNote: {
            textAlign: "center",
        },
    });
};
